import React, { useState } from 'react';
import { motion } from 'framer-motion';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { RAG_CONFIG } from '../config';

const DocumentProcessorTest = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [processingResult, setProcessingResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      setProcessingResult(null);
      setError('');
    }
  };

  const testDocumentProcessing = async () => {
    if (!selectedFile) return;

    setLoading(true);
    setError('');
    setProcessingResult(null);

    try {
      // Create a test file upload
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('subject', 'Test Subject');
      formData.append('class_level', '12');
      formData.append('chapter', 'Test Chapter');
      formData.append('topic', 'Test Topic');
      formData.append('title', selectedFile.name);
      formData.append('user_id', 'test-user-id');
      formData.append('user_name', 'Test User');

      // Upload file
      const uploadResponse = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/v1/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${process.env.REACT_APP_API_KEY || 'test-key'}`
        },
        body: formData
      });

      if (!uploadResponse.ok) {
        throw new Error(`Upload failed: ${uploadResponse.statusText}`);
      }

      const uploadResult = await uploadResponse.json();

      if (!uploadResult.success) {
        throw new Error(uploadResult.error || 'Upload failed');
      }

      // Process for RAG
      const ragResponse = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/v1/rag/process-document`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${process.env.REACT_APP_API_KEY || 'test-key'}`
        },
        body: JSON.stringify({
          file_id: uploadResult.file_id,
          subject: 'Test Subject',
          class_level: '12',
          chapter: 'Test Chapter',
          topic: 'Test Topic',
          user_id: 'test-user-id'
        })
      });

      if (!ragResponse.ok) {
        throw new Error(`RAG processing failed: ${ragResponse.statusText}`);
      }

      const ragResult = await ragResponse.json();

      setProcessingResult({
        upload: uploadResult,
        rag: ragResult,
        file: selectedFile
      });

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getSupportedFormats = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/v1/rag/supported-formats`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${process.env.REACT_APP_API_KEY || 'test-key'}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        return data.supported_formats || [];
      }
      return [];
    } catch (error) {
      console.error('Failed to get supported formats:', error);
      return [];
    }
  };

  const [supportedFormats, setSupportedFormats] = useState([]);

  React.useEffect(() => {
    getSupportedFormats().then(setSupportedFormats);
  }, []);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6">
      <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
        🔧 Document Processing Test
      </h3>
      
      <div className="space-y-4">
        {/* File Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Select Test File
          </label>
          <input
            type="file"
            onChange={handleFileSelect}
            accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.webp,.csv,.json"
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          />
        </div>

        {/* Supported Formats */}
        <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
          <h5 className="font-medium text-blue-900 dark:text-blue-100 mb-2">Supported Formats</h5>
          <div className="flex flex-wrap gap-2">
            {supportedFormats.map((format, index) => (
              <span key={index} className="text-xs bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 px-2 py-1 rounded">
                {format}
              </span>
            ))}
          </div>
        </div>

        {/* Test Button */}
        <button
          onClick={testDocumentProcessing}
          disabled={!selectedFile || loading}
          className="w-full px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 text-white rounded-lg transition-colors flex items-center justify-center gap-2"
        >
          <UploadFileIcon />
          {loading ? 'Processing...' : 'Test Document Processing'}
        </button>

        {/* Error Display */}
        {error && (
          <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <div className="flex items-center gap-2">
              <ErrorIcon className="text-red-500" />
              <p className="text-red-600 dark:text-red-400 text-sm">{error}</p>
            </div>
          </div>
        )}

        {/* Results Display */}
        {processingResult && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-4"
          >
            <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircleIcon className="text-green-500" />
                <h5 className="font-medium text-green-900 dark:text-green-100">Processing Successful!</h5>
              </div>
              
              <div className="space-y-2 text-sm">
                <div>
                  <strong>File:</strong> {processingResult.file.name}
                </div>
                <div>
                  <strong>File Type:</strong> {processingResult.file.type}
                </div>
                <div>
                  <strong>File Size:</strong> {(processingResult.file.size / 1024).toFixed(2)} KB
                </div>
                <div>
                  <strong>Upload Status:</strong> {processingResult.upload.success ? 'Success' : 'Failed'}
                </div>
                <div>
                  <strong>RAG Processing:</strong> {processingResult.rag.success ? 'Success' : 'Failed'}
                </div>
                {processingResult.rag.chunks_created && (
                  <div>
                    <strong>Chunks Created:</strong> {processingResult.rag.chunks_created}
                  </div>
                )}
              </div>
            </div>

            {/* Upload Details */}
            <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <h6 className="font-medium text-gray-900 dark:text-white mb-2">Upload Details</h6>
              <pre className="text-xs text-gray-600 dark:text-gray-400 overflow-x-auto">
                {JSON.stringify(processingResult.upload, null, 2)}
              </pre>
            </div>

            {/* RAG Processing Details */}
            <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <h6 className="font-medium text-gray-900 dark:text-white mb-2">RAG Processing Details</h6>
              <pre className="text-xs text-gray-600 dark:text-gray-400 overflow-x-auto">
                {JSON.stringify(processingResult.rag, null, 2)}
              </pre>
            </div>
          </motion.div>
        )}

        {/* Configuration Info */}
        <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
          <h5 className="font-medium text-purple-900 dark:text-purple-100 mb-2">Processing Configuration</h5>
          <div className="text-xs text-purple-700 dark:text-purple-300 space-y-1">
            <p><strong>Embedding Model:</strong> {RAG_CONFIG.embedding_model}</p>
            <p><strong>Chunk Size:</strong> {RAG_CONFIG.chunk_size}</p>
            <p><strong>Similarity Threshold:</strong> {RAG_CONFIG.similarity_threshold}</p>
            <p><strong>Max Tokens:</strong> {RAG_CONFIG.max_tokens}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentProcessorTest; 